import constate from 'constate';
import { useEffect, useMemo, useState } from 'react';

import { useAuthContext } from './useAuth.js';
import { isDefined } from '../utils/index.js';

const ONE_HOUR_IN_MILLISECONDS = 60 * 60 * 1000;
export enum AuthSessionStatus {
  Active = 'Active',
  Expiring = 'Expiring',
  Expired = 'Expired',
}

function useAuthExpiration() {
  const { user } = useAuthContext().getLoggedIn();

  const sessionExpirationDate = useMemo(
    () => (user.sessionExpirationDate ? new Date(user.sessionExpirationDate) : undefined),
    [user.sessionExpirationDate]
  );
  const [sessionStatus, setSessionStatus] = useState<AuthSessionStatus>(AuthSessionStatus.Active);
  const [sessionActiveCount, setSessionActiveCount] = useState(0);

  useEffect(() => {
    if (!isDefined(sessionExpirationDate)) {
      return;
    }
    const timeToExpirationInMilliseconds = sessionExpirationDate.getTime() - Date.now();
    if (timeToExpirationInMilliseconds <= 0) {
      // FIXME
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setSessionStatus(AuthSessionStatus.Expired);
      return;
    }
    const expiringTimeoutId = setTimeout(
      () => {
        setSessionStatus((currentStatus) => {
          return currentStatus === AuthSessionStatus.Active
            ? AuthSessionStatus.Expiring
            : currentStatus;
        });
      },
      Math.max(0, timeToExpirationInMilliseconds - ONE_HOUR_IN_MILLISECONDS)
    );
    const expiredTimeoutId = setTimeout(() => {
      setSessionStatus(AuthSessionStatus.Expired);
    }, timeToExpirationInMilliseconds);

    return () => {
      clearTimeout(expiredTimeoutId);
      clearTimeout(expiringTimeoutId);
    };
  }, [sessionExpirationDate, sessionActiveCount]);

  /**
   * Browsers may suspend the page when it is not in focus. Similarly, a user may close the browser, or
   * put their device to sleep for several hours or days.
   * In these cases, the timers will not run, and the session status will not be updated.
   * This means that the timer will not be accurate when the user comes back to the page.
   * We want to restart the setTimeout when the user restores window focus.
   * We do this via incrementing the above effect's dependency `sessionActiveCount` state.
   */
  useEffect(() => {
    const onWindowFocus = () => {
      setSessionActiveCount((value) => value + 1);
    };
    window.addEventListener('focus', onWindowFocus);
    return () => {
      window.removeEventListener('focus', onWindowFocus);
    };
  }, []);

  return {
    sessionExpirationDate,
    sessionStatus,
  };
}

// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore-error FIXME: workaround for busted types
export const [AuthExpirationProvider, useAuthExpirationContext] = constate(useAuthExpiration);
